// ABOUTME: Client for communicating with the SQL.js Web Worker
// ABOUTME: Handles message passing and promise-based responses for database operations

import { initTracker } from './initTracker';

interface WorkerMessage {
    id: string;
    type: string;
    payload?: any;
}

interface WorkerResponse {
    id: string;
    success: boolean;
    data?: any;
    error?: string;
}

export class WorkerClient {
    private worker: Worker;
    private pendingRequests = new Map<string, { resolve: (value: any) => void; reject: (error: Error) => void }>();
    private messageIdCounter = 0;

    constructor(workerOrScript: Worker | string) {
        if (workerOrScript instanceof Worker) {
            this.worker = workerOrScript;
        } else {
            this.worker = new Worker(workerOrScript);
        }
        this.worker.addEventListener('message', this.handleMessage.bind(this));
        this.worker.addEventListener('error', this.handleError.bind(this));
    }

    private handleMessage(event: MessageEvent<WorkerResponse | any>): void {
        const messageData = event.data;
        
        // Handle localStorage fallback messages
        if (messageData.type === 'saveToLocalStorage') {
            localStorage.setItem(messageData.key, messageData.data);
            console.log('WorkerClient: Saved data to localStorage for worker');
            return;
        }
        
        if (messageData.type === 'requestFromMainThread') {
            const { requestId, operation, key } = messageData;
            let data = null;
            
            if (operation === 'getFromLocalStorage') {
                data = localStorage.getItem(key);
            }
            
            this.worker.postMessage({
                type: 'response',
                requestId,
                data
            });
            return;
        }
        
        // Handle persistence method notifications
        if (messageData.type === 'persistenceMethod') {
            const { method, operation, success, error, info } = messageData;
            const message = success 
                ? `${operation} using ${method}${info ? ` (${info})` : ''}`
                : `${operation} failed with ${method}${error ? `: ${error}` : ''}`;
            
            initTracker.addEvent('persistence', 'SQLWorker', message, {
                method,
                operation,
                success,
                error,
                info
            });
            return;
        }
        
        // Handle SQL logging
        if (messageData.type === 'sqlLog') {
            const { context, operation, sql, params } = messageData;
            const sqlMessage = `${operation}: ${sql}${params}`;
            console.log('WorkerClient: Received SQL log:', sqlMessage);
            initTracker.addEvent('sql', `SQL-${context}`, sqlMessage, {
                context,
                operation,
                sql,
                params
            });
            return;
        }
        
        // Handle normal worker responses
        const { id, success, data, error } = messageData as WorkerResponse;
        const request = this.pendingRequests.get(id);
        
        if (request) {
            this.pendingRequests.delete(id);
            
            if (success) {
                request.resolve(data);
            } else {
                request.reject(new Error(error || 'Unknown worker error'));
            }
        } else {
            console.warn('Received response for unknown request ID:', id);
        }
    }

    private handleError(event: ErrorEvent): void {
        console.error('Worker error:', event.error);
        
        // Reject all pending requests
        for (const [id, request] of this.pendingRequests.entries()) {
            request.reject(new Error(`Worker error: ${event.error?.message || 'Unknown error'}`));
            this.pendingRequests.delete(id);
        }
    }

    private generateMessageId(): string {
        return `msg_${++this.messageIdCounter}_${Date.now()}`;
    }

    async sendMessage<T = any>(type: string, payload?: any): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const id = this.generateMessageId();
            
            const message: WorkerMessage = {
                id,
                type,
                payload
            };

            this.pendingRequests.set(id, { resolve, reject });
            
            try {
                this.worker.postMessage(message);
            } catch (error) {
                this.pendingRequests.delete(id);
                reject(new Error(`Failed to send message: ${error instanceof Error ? error.message : String(error)}`));
            }
        });
    }

    terminate(): void {
        // Reject all pending requests
        for (const [id, request] of this.pendingRequests.entries()) {
            request.reject(new Error('Worker terminated'));
            this.pendingRequests.delete(id);
        }
        
        this.worker.terminate();
    }
}